import { Dialog, Transition } from '@headlessui/react'
import { cn } from 'design-system'
import { Minimize2 } from 'lucide-react'
import { Fragment, useRef } from 'react'

const Modal = ({
  open = false,
  icon,
  title,
  caption,
  large,
  fullScreen,
  transparent,
  classNames = {},
  onRequestClose,
  children,
  initialFocus,
  ...restProps
}) => {
  const panelRef = useRef()

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className={cn('fixed inset-0 z-10', {
          'overflow-y-auto': !fullScreen
        })}
        data-testid={restProps['data-testid']}
        onClose={onRequestClose}
        initialFocus={initialFocus || panelRef}>
        <div
          className={cn(
            'flex min-h-full items-center justify-center overflow-hidden sm:p-12',
            { 'h-screen w-screen': fullScreen },
            classNames.wrapper
          )}>
          <div className="fixed inset-0 bg-black/30" />
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95">
            <Dialog.Panel
              ref={panelRef}
              tabIndex={-1}
              className={cn({
                'relative flex max-w-full flex-col rounded-lg shadow-xl': true,
                'w-96': !large && !fullScreen,
                'w-156': large,
                'h-full w-full': fullScreen,
                'bg-white': !transparent,
                [classNames.body]: classNames.body
              })}>
              <div className="flex rounded-t-lg bg-slate-50 p-4">
                <div className="flex flex-auto items-center gap-2">
                  <span className="shrink-0">{icon}</span>
                  <div className="flex-1">
                    {title ? (
                      <Dialog.Title
                        as="h2"
                        className="break-words text-base font-medium">
                        {title}
                      </Dialog.Title>
                    ) : null}
                    {caption ? (
                      <Dialog.Description className="text-base">
                        {caption}
                      </Dialog.Description>
                    ) : null}
                  </div>
                </div>
                <button
                  className={cn({
                    'group flex h-12 w-12 shrink-0 items-center justify-center rounded-full': true,
                    'bg-slate-100 text-lg text-slate-900 hover:text-slate-50 focus:text-slate-50': true,
                    'transition-all hover:bg-slate-900 focus:bg-slate-900 focus:outline-none': true,
                    'focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2': true
                  })}
                  aria-label="Close dialog"
                  onClick={onRequestClose}>
                  <Minimize2 className="transition-all group-hover:scale-75 group-focus:scale-75" />
                </button>
              </div>
              {children}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}

export default Modal
